
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Wand2, Sparkles } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";

interface GenerateContentProps {
  onContentGenerated: (content: any) => void;
  formHasData: boolean;
  isLoading: boolean;
}

export function GenerateContent({ onContentGenerated, formHasData, isLoading }: GenerateContentProps) {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt to generate content");
      return;
    }

    try {
      setIsGenerating(true);
      
      // Call the Make.com webhook with the prompt
      const response = await fetch("https://hook.eu2.make.com/p8qf6ycc6smmga3tbct5zv40qqv5z95t", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to generate content: ${response.statusText}`);
      }
      
      const data = await response.json();
      setGeneratedContent(data);
      
      // If the form already has data, ask for confirmation before overwriting
      if (formHasData) {
        setShowConfirmDialog(true);
      } else {
        // No existing data, directly apply the generated content
        onContentGenerated(data);
        toast.success("Content generated successfully!");
      }
    } catch (error) {
      console.error("Error generating content:", error);
      toast.error("Failed to generate content. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const confirmOverwrite = () => {
    onContentGenerated(generatedContent);
    setShowConfirmDialog(false);
    toast.success("Content applied successfully!");
  };

  const cancelOverwrite = () => {
    setShowConfirmDialog(false);
    setGeneratedContent(null);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 border border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="h-6 w-6 text-primary" />
          <h3 className="text-xl font-medium">What would you like to write about?</h3>
        </div>
        
        <div className="mb-6">
          <p className="text-sm text-slate-600 mb-6">
            Describe the topic, tone, and purpose of your blog post. The AI will generate a complete draft for you,
            including title, content, excerpt, and SEO metadata.
          </p>
          
          <Textarea
            id="generate-prompt"
            placeholder="E.g., Write a comprehensive guide about sustainable gardening practices for urban environments, focusing on water conservation and organic pest control. Target audience is beginners with limited space."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="min-h-36 text-base"
            disabled={isGenerating || isLoading}
          />
        </div>

        <div className="flex justify-end">
          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating || !prompt.trim() || isLoading}
            size="lg"
            className="gap-2 px-6"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" />
                Generate Content
              </>
            )}
          </Button>
        </div>
      </Card>

      {formHasData && (
        <Card className="p-4 bg-amber-50 border border-amber-200">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-alert-circle"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Generating new content will replace your current post data. Images and scheduled publication dates will not be affected.
          </p>
        </Card>
      )}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite existing content?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current post data with the newly generated content.
              Images and scheduled publication dates will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelOverwrite}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOverwrite}>Apply Generated Content</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
